import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { prefiereMenosMovimiento } from './movimiento'

/**
 * Cuánto dura la lluvia antes de retirarse del DOM. Pasado ese tiempo las
 * piezas se desmontan: una celebración que se queda puesta deja de ser una
 * celebración (y son 36 nodos animados sobre un diálogo que se sigue usando).
 */
export const DURACION_CONFETI_MS = 3200

const COLORES = ['#F97316', '#0F2C4C', '#FACC15', '#22C55E', '#38BDF8', '#EC4899']

// Piezas DETERMINISTAS (no `Math.random` en el render): mismo confeti en cada
// apertura, sin que un re-render las recoloque a media caída y sin que la suite
// dependa del azar.
const PIEZAS = Array.from({ length: 36 }, (_, i) => ({
  izquierda: (i * 97) % 100,
  retraso: ((i * 137) % 900) / 1000,
  duracion: 1.8 + ((i * 53) % 120) / 100,
  giro: (i * 47) % 360,
  ancho: 5 + (i % 4),
  alto: 9 + (i % 5),
  color: COLORES[i % COLORES.length],
}))

interface ConfetiProps {
  /** true = el aviso está abierto AHORA. Un cambio a true dispara una lluvia. */
  activo: boolean
}

/**
 * El confeti del aviso de novedades (goal portal-correcciones 10, decisión 2).
 *
 * 🚨 **DOM + CSS, sin librería**, calcado del de la trivia de `comunidad-web`
 * (`src/feature/trivia/components/Confeti.tsx`), que tomó esa decisión
 * midiendo: `canvas-confetti` pinta sobre un `<canvas>` y en el jsdom de la
 * suite `getContext('2d')` devuelve `null`, así que habría que mockearla y los
 * tests que este goal EXIGE («sale al abrir», «no sale con movimiento
 * reducido») pasarían a probar el mock en vez de la pantalla.
 *
 * 🚨 **Se pinta en un portal sobre `document.body`, no dentro del diálogo**: el
 * `DialogContent` de Radix recorta (`overflow-hidden`) y vive en su propio
 * portal con `z-50`. A pantalla completa y con `z-[60]` la lluvia cae por
 * encima del overlay Y del contenido, sin que el modal la corte.
 *
 * 🚨 **No captura clics** (`pointer-events-none`): con 36 nodos fijos encima,
 * cualquier otra cosa dejaría la equis de cerrar inalcanzable durante 3,2 s.
 */
export function Confeti({ activo }: ConfetiProps) {
  const [terminado, setTerminado] = useState(false)
  // Inicialización PEREZOSA y no `false`: aquí el componente nace ya activo
  // —el aviso se monta cuando el servidor contesta, o sea abierto de una vez—,
  // así que si la preferencia solo se leyera en la transición, la primera (y
  // normalmente única) lluvia se saltaría la guarda.
  const [menosMovimiento, setMenosMovimiento] = useState(() =>
    activo ? prefiereMenosMovimiento() : false,
  )
  const [activoPrevio, setActivoPrevio] = useState(activo)

  // Patrón «estado del render anterior» (react.dev): la transición se resuelve
  // en el RENDER y no en un efecto que llame a setState — eso encadena renders
  // y el lint de hooks lo rechaza.
  //
  // Y la preferencia se relee AQUÍ, al abrir, no al montar: si alguien la
  // cambia en su sistema con la pestaña abierta, manda la de ahora.
  if (activoPrevio !== activo) {
    setActivoPrevio(activo)
    setTerminado(false)
    setMenosMovimiento(activo ? prefiereMenosMovimiento() : false)
  }

  const cayendo = activo && !terminado && !menosMovimiento

  // El efecto solo RETIRA la lluvia pasado su tiempo (setState en el callback
  // del temporizador, no en el cuerpo del efecto).
  useEffect(() => {
    if (!cayendo) return
    const temporizador = setTimeout(() => setTerminado(true), DURACION_CONFETI_MS)
    return () => clearTimeout(temporizador)
  }, [cayendo])

  if (!cayendo || typeof document === 'undefined') return null

  return createPortal(
    <div
      data-testid="confeti"
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
    >
      {PIEZAS.map((pieza, i) => (
        <span
          key={i}
          className="confeti-pieza"
          style={{
            left: `${pieza.izquierda}%`,
            width: `${pieza.ancho}px`,
            height: `${pieza.alto}px`,
            backgroundColor: pieza.color,
            animationDelay: `${pieza.retraso}s`,
            animationDuration: `${pieza.duracion}s`,
            transform: `rotate(${pieza.giro}deg)`,
          }}
        />
      ))}
    </div>,
    document.body,
  )
}
